import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setActiveLeagueId, clearActiveLeague } from "@/lib/league";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import HowToPlayModal from "@/components/HowToPlayModal";
import ManifestoModal from "@/components/ManifestoModal";

export default function WelcomeScreen() {
  const nav = useNavigate();
  const [howTo, setHowTo] = useState(false);
  const [manifesto, setManifesto] = useState(false);
  const [mode, setMode] = useState(null); // null | "code"
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const enterByCode = async (e) => {
    e?.preventDefault?.();
    const clean = code.replace(/\D/g, "").slice(0, 6);
    if (clean.length !== 6) {
      toast.error("League code must be 6 digits");
      return;
    }
    setBusy(true);
    try {
      const league = await api.getLeagueByCode(clean);
      setActiveLeagueId(league.id);
      toast.success(`Joined ${league.name}`);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No league with that code");
    } finally {
      setBusy(false);
    }
  };

  const startCreateNew = () => {
    // Decouple from any active league so Setup begins blank
    clearActiveLeague();
    nav("/setup?new=1");
  };

  return (
    <div className="min-h-screen flex flex-col mobile-bottom-pad">
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(circle at 20% 20%, rgba(0,229,255,0.18), transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,20,147,0.18), transparent 45%)",
        }}
      />

      <header className="relative px-6 md:px-12 pt-10">
        <BrandLogo size="md" />
      </header>

      <main className="relative flex-1 flex items-center px-6 md:px-12 py-10">
        <div className="w-full max-w-3xl mx-auto">
          <div className="label-mono" style={{ color: "var(--neon-blue)" }}>WELCOME TO</div>
          <h1 className="font-montserrat uppercase mt-2 leading-[0.85]">
            <span className="brand-wordmark text-6xl sm:text-7xl md:text-8xl">STAKES</span>
          </h1>
          <p className="font-barlow text-xl md:text-2xl uppercase text-[var(--stakes-muted)] mt-5 max-w-xl">
            High-stakes family habit league.<br/>
            <span className="text-white">Set the rules. Snap the proof. Win the pot.</span>
          </p>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <button
              onClick={startCreateNew}
              className="min-h-[96px] py-4 flex flex-col justify-center btn-neon-blue font-montserrat uppercase text-xl md:text-2xl tracking-wider px-6 text-left leading-tight"
              data-testid="welcome-create-button"
            >
              <span>+ CREATE A NEW LEAGUE</span>
              <span className="label-mono mt-2 normal-case" style={{ color: "var(--stakes-muted)" }}>YOU&apos;LL BE THE ADMIN</span>
            </button>

            {mode === "code" ? (
              <form
                onSubmit={enterByCode}
                className="border-2 border-[var(--neon-pink)] p-4 flex flex-col gap-3"
                style={{ boxShadow: "0 0 24px rgba(255,20,147,0.25), inset 0 0 30px rgba(255,20,147,0.06)", scrollMarginTop: "20vh" }}
              >
                <div className="label-mono" style={{ color: "var(--neon-pink)" }}>ENTER 6-DIGIT CODE</div>
                <input
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onFocus={(e) => {
                    setTimeout(() => {
                      try { e.target.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ }
                    }, 300);
                  }}
                  placeholder="000000"
                  style={{ fontSize: "max(2.5rem, 16px)" }}
                  className="bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--neon-pink)] outline-none font-anton text-center tracking-[0.4em] pb-2"
                  data-testid="welcome-code-input"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setMode(null)}
                    className="flex-1 h-12 label-mono border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
                    data-testid="welcome-code-cancel"
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    disabled={busy}
                    className="flex-1 h-12 btn-neon-pink font-barlow font-black uppercase tracking-wider"
                    data-testid="welcome-code-submit"
                  >
                    {busy ? "JOINING…" : "JOIN LEAGUE →"}
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setMode("code")}
                className="min-h-[96px] py-4 flex flex-col justify-center btn-neon-pink font-montserrat uppercase text-xl md:text-2xl tracking-wider px-6 text-left leading-tight"
                data-testid="welcome-enter-code-button"
              >
                <span># ENTER LEAGUE CODE</span>
                <span className="label-mono mt-2 normal-case" style={{ color: "var(--stakes-muted)" }}>JOIN AN EXISTING LEAGUE</span>
              </button>
            )}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => setHowTo(true)}
              className="label-mono px-4 h-12 border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
              data-testid="welcome-howto-button"
            >
              ❓ HOW TO PLAY
            </button>
            <button
              onClick={() => setManifesto(true)}
              className="label-mono px-4 h-12 border border-[var(--stakes-primary)] text-[var(--stakes-primary)] hover:bg-[var(--stakes-primary)] hover:text-black hover-flip"
              data-testid="welcome-manifesto-button"
            >
              ⚡ THE WHY
            </button>
          </div>
        </div>
      </main>

      <footer className="relative px-6 md:px-12 pb-8 label-mono text-[var(--stakes-muted)] flex flex-wrap items-center justify-between gap-2">
        <span>Multi-league · standalone app · powered by stakes</span>
        <span data-testid="welcome-system-timezone" className="font-mono uppercase">
          SYSTEM TIMEZONE · {(() => {
            try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
          })()}
        </span>
      </footer>

      <HowToPlayModal open={howTo} onClose={() => setHowTo(false)} />
      <ManifestoModal open={manifesto} onClose={() => setManifesto(false)} />
    </div>
  );
}
